import { ComputedReferenceNodeModel } from "@/models/nodes/ComputedReferenceNodeModel";
import { displayHotkey } from "@/utils/hotkeys";

import type { IconName } from "@/components/icons/Icon";
import type {
  ComputedCommand,
  ComputedCommandSuggestion,
  SuggestionProvider,
} from "@/components/suggestions/types";
import type { Hotkey } from "@/utils/hotkeys";

const COMMAND_ICON: Record<ComputedCommand, IconName> = {
  unwrap: "math/Ungroup",
  "go-to-source": "Link",
};

const COMMAND_HOTKEY: Partial<Record<ComputedCommand, Hotkey>> = {
  "go-to-source": "go-to-source",
};

/**
 * Context-menu commands for the selected computed node: `unwrap` for both kinds,
 * plus `go-to-source` for a reference. A dangling reference has nowhere to jump
 * to, so the latter is dropped when the target wrapper is gone or disabled.
 */
export const computedCommandSuggestionsProvider: SuggestionProvider<"computedCommand"> = {
  type: "computedCommand",
  search: (_str, doc) => {
    const node = doc.computedNodes.selectedComputedNode;
    if (!node) {
      return [];
    }

    const commands: ComputedCommand[] = ["unwrap"];

    if (node.is(ComputedReferenceNodeModel) && doc.computedNodes.getSourceWrapper(node)) {
      commands.push("go-to-source");
    }

    const suggestions: ComputedCommandSuggestion[] = commands.map((command) => ({
      type: "computedCommand",
      command,
      trackingId: command,
      popupGroup: "computed",
      // above the clipboard group, which sits at priority 1
      priority: 2,
      icon: COMMAND_ICON[command],
      hotkey: COMMAND_HOTKEY[command] && displayHotkey(COMMAND_HOTKEY[command]),
    }));

    return [suggestions];
  },
};
